<template>
  <nav class="breadcrumbs" aria-label="Breadcrumb">
    <ol class="breadcrumbs-list">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="breadcrumbs-item"
      >
        <template v-if="item.type === 'program'">
          <select
            v-model="selectedProgram"
            @change="showSwitchProgramConfirmationModal"
            class="breadcrumbs-select"
          >
            <optgroup label="Active Programs">
              <option
                v-for="program in activePrograms"
                :key="program.id"
                :value="program.id"
              >
                {{ program.name }}
              </option>
            </optgroup>

            <optgroup
              label="Inactive Programs"
              v-if="inactivePrograms.length"
            >
              <option
                v-for="program in inactivePrograms"
                :key="program.id"
                :value="program.id"
                disabled
              >
                {{ program.name }}
              </option>
            </optgroup>
          </select>
          <span class="breadcrumbs-separator" v-if="index < items.length - 1">/</span>
        </template>

        <template v-else-if="item.type === 'child'">
          <select
            v-model="selectedChildID"
            @change="handleChildChange"
            class="breadcrumbs-select"
          >
            <optgroup label="Available">
              <option
                v-for="child in availableChildren"
                :key="child.id"
                :value="child.id"
              >
                {{ child.full_name }}
              </option>
            </optgroup>

            <optgroup label="Unavailable">
              <option
                v-for="child in unavailableChildren"
                :key="child.id"
                :value="child.id"
                disabled
              >
                {{ child.full_name }}
              </option>
            </optgroup>

            <option :value="'add_new'" class="add-child-option btn btn--color-dark">
              Add A Child
            </option>
          </select>
          <span class="breadcrumbs-separator" v-if="index < items.length - 1">/</span>
        </template>

        <template v-else-if="index < items.length - 1">
          <router-link
            :to="{ name: item.name }"
            class="breadcrumbs-link"
          >
            {{ item.label }}
          </router-link>
          <span class="breadcrumbs-separator">/</span>
        </template>

        <template v-else>
          <span class="breadcrumbs-current">{{ item.label }}</span>
        </template>
      </li>
    </ol>

    <switch-child-info-modal
      v-if="potentialChild"
      :new_child_name="potentialChild.full_name"
      :previous_child_name="selectedChild.full_name"
      @cancel="cancelSwitchingChild"
      @confirm="acceptSwitchingChild"
    />

    <switch-program-info-modal
      v-if="potentialProgram && currentProgram"
      :new_program_name="potentialProgram.name"
      :previous_program_name="currentProgram.name"
      @cancel="cancelSwitchingProgram"
      @confirm="goToProgram"
    />

    <add-new-child-modal
      :is-show-modal="isShowAddNewChildModal"
      :default-gender="defaultProgramGender"
      :is-enable-select-gender="enableSelectGender"
      :family-name="parent.last_name"
      :family-id="parent.family_id"
      @closeModal="closeModal"
      @updateChildrenList="acceptAddingChild"
    />

  </nav>
</template>

<script>
import { mapGetters } from 'vuex'
import { getUserData } from '@/auth/utils'
import { prepareProgramRegistrationInfo } from '@core/mixins/prepareProgramRegistrationInfo'
import SwitchChildInfoModal from '@/components/site/breadcrumb/SwitchChildInfoModal.vue'
import SwitchProgramInfoModal from '@/components/site/breadcrumb/SwitchProgramInfoModal.vue'
import AddNewChildModal from '@/components/site/add-new-child-modal/AddNewChildModal.vue'

export default {
  name: 'Breadcrumbs',
  components: {
    AddNewChildModal,
    SwitchProgramInfoModal,
    SwitchChildInfoModal,
  },
  mixins: [ prepareProgramRegistrationInfo ],
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      programsList: 'programs/getProgramsForSelectList',
      hasChildrenLoaded: 'children/hasChildrenLoaded',
      childrenList: 'children/getChildrenList',
      authSettings: 'authSettings/getAuthSettings',
    }),
    activePrograms() {
      return this.programsList.filter(p => p.is_active_for_camper)
    },
    inactivePrograms() {
      return this.programsList.filter(p => !p.is_active_for_camper)
    },
    availableChildren() {
      return this.childrenList.filter(p => !p.isDisabled && !p.ageIsEmpty && !p.dynamicFieldIsEmpty)
    },
    unavailableChildren() {
      return this.childrenList.filter(p => p.isDisabled || p.ageIsEmpty || p.dynamicFieldIsEmpty)
    },
  },
  data() {
    return {
      selectedProgram: null,
      selectedProgramID: null,
      selectedChild: null,
      selectedChildID: null,
      potentialChild: null,
      potentialProgram: null,
      currentProgram: null,

      parent: getUserData(),
      isShowAddNewChildModal: false,
      defaultProgramGender: null,
      enableSelectGender: false,
      newChild: null,
    }
  },
  async mounted() {
    await this.$store.dispatch('programs/fetchProgramForSelectList')
    const currentProgram = this.programsList.find(p => p.id === this.items.find(i => i.type === 'program')?.id)
    if (currentProgram) {
      this.selectedProgram = currentProgram.id
      this.selectedProgramID = currentProgram.id

      this.defaultProgramGender = this.getDefaultGenderForProgram(currentProgram.gender, this.authSettings)
      this.enableSelectGender = currentProgram.gender === 'Both'
    }


    if (!this.hasChildrenLoaded) {
      await this.$store.dispatch('children/fetchChildrenList')
    }

    if (currentProgram) {
      this.$store.commit('children/SET_SELECTED_PROGRAM_AVAILABILITY_SETTINGS', currentProgram)
      await this.$store.dispatch('children/updateChildrenListWithProgramAccess')
    }

    const currentChild = this.childrenList.find(p => p.id == this.items.find(i => i.type === 'child')?.id)
    if (currentChild) {
      this.selectedChild = currentChild
      this.selectedChildID = currentChild.id
    }
  },
  methods: {
    showSwitchProgramConfirmationModal() {
      this.potentialProgram = this.programsList.find(p => p.id === this.selectedProgram)
      this.currentProgram = this.programsList.find(p => p.id === this.selectedProgramID)

      this.$nextTick(() => {
        this.$bvModal.show('switch-program-info-modal')
      })
    },
    cancelSwitchingProgram() {
      this.potentialProgram = null
      this.currentProgram = null
      this.selectedProgram = this.selectedProgramID

      this.$bvModal.hide('switch-program-info-modal')
    },
    goToProgram() {
      this.$bvModal.hide('switch-program-info-modal')
      if (this.potentialProgram) {
        this.$router.push({ name: 'application-select-child', params: { id: this.potentialProgram.id }})
      }
    },
    handleChildChange(event) {
      const selected = event.target.value

      if (selected === 'add_new') {
        this.isShowAddNewChildModal = true
      } else {
        this.showSwitchChildConfirmationModal()
      }
    },
    closeModal() {
      this.selectedChildID = this.selectedChild.id
      this.isShowAddNewChildModal = false
    },
    async acceptAddingChild(newId) {
      await this.$store.dispatch('children/fetchChildrenList')
      await this.$store.dispatch('children/updateChildrenListWithProgramAccess')
      this.newChild = this.childrenList.find(p => p.id == newId)

      if (await this.checkIsDataMissing(this.newChild)) {
        this.newChild.isDisabled = true
      } else {
        this.selectedChild = this.newChild
        this.selectedChildID = this.newChild.id
        await this.goToApplicationCreation(this.newChild)
      }
    },
    async checkIsDataMissing(child) {
      return child.ageIsEmpty || child.dynamicFieldIsEmpty
    },
    showSwitchChildConfirmationModal() {
      this.potentialChild = this.childrenList.find(p => p.id == this.selectedChildID)

      this.$nextTick(() => {
        this.$bvModal.show('switch-child-info-modal')
      })
    },
    cancelSwitchingChild() {
      this.potentialChild = null
      this.selectedChildID = this.selectedChild.id

      this.$bvModal.hide('switch-child-info-modal')
    },
    async acceptSwitchingChild() {
      this.$bvModal.hide('switch-child-info-modal')
      await this.goToApplicationCreation(this.potentialChild)
    },
    async goToApplicationCreation(child) {
      if (child && this.selectedProgram) {
        await this.$store.dispatch('children/goToApplicationPage', {
          child: child,
          programId: this.selectedProgram,
        })
      }
    },
  },
}
</script>


<style scoped lang="scss">

</style>